import { useState } from "react";
import { api } from "../lib/api";
import { useFetch } from "../lib/useFetch";

interface Me { id: number; name: string; email: string; role: string; orgName: string | null }
interface Prefs { inApp: boolean; email: boolean; push: boolean }

export default function ProfileSettings() {
  const me = useFetch<Me>("/v1/me");
  const prefs = useFetch<Prefs>("/v1/me/notification-prefs");
  const [name, setName] = useState<string | null>(null);
  const [pw, setPw] = useState({ currentPassword: "", newPassword: "" });
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function saveName(e: React.FormEvent) {
    e.preventDefault();
    setErr(null); setMsg(null);
    try {
      await api("/v1/me", { method: "PATCH", body: JSON.stringify({ name: name ?? me.data?.name }) });
      setMsg("Profile updated"); me.reload();
    } catch (e2) { setErr((e2 as Error).message); }
  }
  async function changePassword(e: React.FormEvent) {
    e.preventDefault();
    setErr(null); setMsg(null);
    try {
      await api("/v1/me/password", { method: "POST", body: JSON.stringify(pw) });
      setPw({ currentPassword: "", newPassword: "" }); setMsg("Password changed");
    } catch (e2) { setErr((e2 as Error).message); }
  }

  async function toggle(channel: keyof Prefs) {
    if (!prefs.data) return;
    const next = { ...prefs.data, [channel]: !prefs.data[channel] };
    await api("/v1/me/notification-prefs", { method: "PUT", body: JSON.stringify(next) });
    prefs.reload();
  }

  if (!me.data) return null;
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-medium text-ink">Profile</h2>
      <div className="card p-5 space-y-4 max-w-xl">
        <p className="text-sm text-mut">{me.data.email} · <span className="capitalize">{me.data.role}</span>{me.data.orgName ? ` · ${me.data.orgName}` : ""}</p>
        <form onSubmit={saveName} className="flex items-end gap-2">
          <label className="block flex-1"><span className="label">Display name</span>
            <input value={name ?? me.data.name} onChange={(e) => setName(e.target.value)} className="input" /></label>
          <button className="btn-dark">Save</button>
        </form>
        <form onSubmit={changePassword} className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-end border-t border-edge-soft pt-4">
          <label className="block"><span className="label">Current password</span>
            <input type="password" value={pw.currentPassword} onChange={(e) => setPw({ ...pw, currentPassword: e.target.value })} className="input" /></label>
          <label className="block"><span className="label">New password</span>
            <input type="password" value={pw.newPassword} onChange={(e) => setPw({ ...pw, newPassword: e.target.value })} className="input" /></label>
          <button className="btn-secondary px-3 py-2">Change password</button>
        </form>
        {msg && <p className="text-sm text-teal-700">{msg}</p>}
        {err && <p className="text-sm text-red-600">{err}</p>}
      </div>
      {prefs.data && (
        <div className="card p-5 space-y-2 max-w-xl">
          <p className="text-sm font-medium text-ink">Notification channels</p>
          {(["inApp", "email", "push"] as const).map((ch) => (
            <label key={ch} className="flex items-center gap-2 text-sm text-body">
              <input type="checkbox" checked={prefs.data![ch]} onChange={() => toggle(ch)} />
              {ch === "inApp" ? "In-app" : ch === "email" ? "Email" : "Push"}
            </label>
          ))}
          <p className="text-xs text-faint">Email and push delivery attach to these preferences in production; in-app is always recorded.</p>
        </div>
      )}
    </section>
  );
}
